import {Button, ButtonSharedProps, Tooltip} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {isEmpty} from "lodash";
import {memo, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {AppContext} from "../../../ApplicationContext";
import {Cords, getMapBounds} from "../MappingService";
import {MapPopOutIcon, ZoomAllIcon, ZoomDriversIcon, ZoomInIcon, ZoomJobsIcon, ZoomOutIcon} from "./MapIcons";
import {Constants} from "../../../constants/constants";

export const reZoom = (map: any) => {
  const currentZoom = map.getZoom();
  if (currentZoom && currentZoom > 15) {
    map.setZoom(15);
  }
};

export const paddingBounds = {
  top: 103,
  right: 63,
  bottom: 74,
  left: 47
};

export type MapReframeProps = {
  driverCords: Cords[];
  jobCords: Cords[];
  stopCords: Cords[];
  routeCords: Cords[];
  maps: any;
  map: any;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  newWindow?: Window;
  usePortal?: boolean;
  isPortalOpen?: boolean;
  onPopOut?: () => void;
};

type ReframeButtonProps = ButtonSharedProps & {
  dataTestId?: string;
  tooltipContent?: string;
  newWindow?: Window;
  usePortal?: boolean;
};

const ReframeButton = ({
  dataTestId,
  tooltipContent,
  newWindow,
  usePortal,
  disabled,
  onClick,
  icon
}: ReframeButtonProps) => {
  return (
    <Tooltip
      popoverClassName="map-action-popover"
      content={tooltipContent}
      usePortal={usePortal}
      portalContainer={newWindow?.document.body}
    >
      <StyledButton data-testid={dataTestId} disabled={disabled} active={!disabled} onClick={onClick} icon={icon} />
    </Tooltip>
  );
};

const MapReframe = ({
  driverCords,
  jobCords,
  stopCords,
  routeCords,
  maps,
  map,
  onZoomIn,
  onZoomOut,
  newWindow,
  usePortal,
  isPortalOpen,
  onPopOut
}: MapReframeProps) => {
  const [zoomLevel, setZoomLevel] = useState<number>(Constants.MAP.DEFAULT_ZOOM);
  const appContext = useContext(AppContext);

  useEffect(() => {
    if (!maps) return;
    maps.event.addListener(map, "zoom_changed", () => {
      const currentZoom = map.getZoom();
      setZoomLevel(currentZoom);
    });
    return () => {
      maps.event.clearListeners(map, "zoom_changed");
    };
  }, [map, maps]);

  const seeDrivers = useCallback(() => {
    if (map && driverCords && !isEmpty(driverCords)) {
      const bounds = getMapBounds(maps, driverCords);
      map.fitBounds(bounds, paddingBounds);
    }
    reZoom(map);
  }, [driverCords, map, maps]);

  const seePackages = useCallback(() => {
    if (map && jobCords && !isEmpty(jobCords)) {
      const bounds = getMapBounds(maps, jobCords);
      map.fitBounds(bounds, paddingBounds);
      reZoom(map);
    }
  }, [jobCords, map, maps]);

  const seeAll = useCallback(() => {
    if (map && maps) {
      const bounds = getMapBounds(
        maps,
        [...jobCords, ...driverCords, ...stopCords, ...routeCords].filter((cord) => cord)
      );
      map.fitBounds(bounds, paddingBounds);
      reZoom(map);
    }
  }, [driverCords, jobCords, map, maps, routeCords, stopCords]);

  const zoomIn = useCallback(() => {
    const currentZoom = map?.getZoom();
    if (currentZoom < 22) {
      map.setZoom(currentZoom + 1);
    }
    if (onZoomIn) onZoomIn();
  }, [map, onZoomIn]);

  const zoomOut = useCallback(() => {
    const currentZoom = map?.getZoom();
    if (currentZoom > 3) {
      map.setZoom(currentZoom - 1);
    }
    if (onZoomOut) onZoomOut();
  }, [map, onZoomOut]);

  const handleOpenMapPortal = useCallback(() => {
    if (onPopOut) {
      onPopOut();
    } else {
      appContext.dispatch({
        type: "SetOpenMapPortal",
        payload: true
      });
    }
  }, [appContext, onPopOut]);

  const showPopOutMapButton = useMemo(
    () => !(isPortalOpen ?? appContext.appState.isMapPortalOpen),
    [isPortalOpen, appContext.appState.isMapPortalOpen]
  );
  return (
    <Container data-testid="map-reframe">
      <ReframeContainer>
        {showPopOutMapButton && (
          <ReframeButton
            dataTestId="pop-out-map"
            icon={<MapPopOutIcon />}
            tooltipContent="Pop Out Map"
            onClick={handleOpenMapPortal}
          />
        )}

        <ReframeButton
          dataTestId="see-all"
          disabled={isEmpty(jobCords) && isEmpty(driverCords)}
          icon={<ZoomAllIcon />}
          tooltipContent="Reframe to see All"
          onClick={seeAll}
          newWindow={newWindow}
          usePortal={usePortal}
        />
        <ReframeButton
          dataTestId="see-all-drivers"
          disabled={isEmpty(driverCords)}
          icon={<ZoomDriversIcon />}
          tooltipContent="Reframe to see All Drivers"
          onClick={seeDrivers}
          newWindow={newWindow}
          usePortal={usePortal}
        />
        <ReframeButton
          dataTestId="see-all-jobs"
          disabled={isEmpty(jobCords)}
          icon={<ZoomJobsIcon />}
          tooltipContent="Reframe to see All Jobs"
          onClick={seePackages}
          newWindow={newWindow}
          usePortal={usePortal}
        />
      </ReframeContainer>
      <ReframeContainer>
        <ReframeButton dataTestId="zoom-in" disabled={zoomLevel >= 22} icon={<ZoomInIcon />} onClick={zoomIn} />
        <ReframeButton dataTestId="zoom-out" disabled={zoomLevel <= 3} icon={<ZoomOutIcon />} onClick={zoomOut} />
      </ReframeContainer>
    </Container>
  );
};

export default memo(MapReframe);

const Container = styled.div`
  position: absolute;
  z-index: 1000;
  bottom: 50px;
  left: max(12px, calc(100% - 52px));
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
`;

const ReframeContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 0px 4px 4px 0px #00000040;
  border-radius: 10px;
  overflow: hidden;
`;

const StyledButton = styled(Button)`
  width: 40px;
  height: 45px;
  border: 1px solid #ededed !important;
  box-shadow: none !important;

  :hover {
    &.bp5-button.bp5-active {
      background: linear-gradient(180deg, #ededed 0%, #ededed 100%) !important;
    }
  }

  &.bp5-button:not([class*="bp5-intent-"]) {
    background: white !important;

    path {
      fill: #ededed;
    }
  }

  &.bp5-button.bp5-active {
    background: white !important;

    path {
      fill: #161616;
    }
  }
`;
